/**
 * Role 16 — Statement of Comprehensive Income before Tax — calculated totals.
 *
 * Extracted verbatim from the former single-class `CalculationService`, which held
 * all 13 sheets' total calculations in one 5,557-line file. Each function is pure and
 * self-contained (no shared state, no `this`), so the split is a move, not a rewrite.
 * `CalculationService` remains the injectable entry point and delegates here.
 */

/**
 * Calculate all Role16 (420000 - Statement of Comprehensive Income Before Tax) totals.
 *
 * All qnames are `ssmt:` prefixed for this sheet (no MFRS/MPERS variation).
 * The component owns column-visibility resolution via `resolveRole16ColumnSuffixes`
 * and passes the resolved suffix list in — this keeps layout logic in one place.
 *
 * @param formData - The form data object (control name → value)
 * @param suffixes - Resolved column suffixes, e.g. `['[Consolidated][CurrentYear]', …]`
 * @returns Object containing only the calculated total fields
 */
export function calculateTotals_420000(
  formData: Record<string, any>,
  suffixes: ReadonlyArray<string>,
): Record<string, any> {
  const calculatedTotals: Record<string, any> = {};

  const num = (key: string): number => {
    const raw = formData[key];
    if (raw === null || raw === undefined || raw === '') return 0;
    const parsed = typeof raw === 'number' ? raw : parseFloat(String(raw));
    return Number.isFinite(parsed) ? parsed : 0;
  };

  for (const suffix of suffixes) {
    // 1. Total Items NOT Reclassified (net of tax)
    const itemsNotReclassifiedBeforeTax =
      num(`ssmt:RemeasurementOfDefinedBenefitPlansBeforeTax${suffix}`) +
      num(`ssmt:FairValueChangeOnEquityInvestmentsFVOCIBeforeTax${suffix}`) +
      num(
        `ssmt:RevaluationSurplusOnPropertyPlantAndEquipmentBeforeTax${suffix}`,
      ) +
      num(`ssmt:ShareOfOCIOfAssociatesNotReclassifiedBeforeTax${suffix}`);
    const taxNotReclassified = num(
      `ssmt:TaxRelatingToItemsNotReclassified${suffix}`,
    );
    const totalNotReclassifiedNetOfTax =
      itemsNotReclassifiedBeforeTax - taxNotReclassified;
    calculatedTotals[`ssmt:TotalItemsNotReclassifiedNetOfTax${suffix}`] =
      totalNotReclassifiedNetOfTax;

    // 2. Total Items MAY BE Reclassified (net of tax)
    const itemsMayBeReclassifiedBeforeTax =
      num(
        `ssmt:ExchangeDifferencesOnTranslatingForeignOperationsBeforeTax${suffix}`,
      ) +
      num(`ssmt:CashFlowHedgesBeforeTax${suffix}`) +
      num(`ssmt:FairValueChangeOnDebtInstrumentsFVOCIBeforeTax${suffix}`) +
      num(`ssmt:ShareOfOCIOfAssociatesMayBeReclassifiedBeforeTax${suffix}`);
    const taxMayBeReclassified = num(
      `ssmt:TaxRelatingToItemsMayBeReclassified${suffix}`,
    );
    const totalMayBeReclassifiedNetOfTax =
      itemsMayBeReclassifiedBeforeTax - taxMayBeReclassified;
    calculatedTotals[`ssmt:TotalItemsMayBeReclassifiedNetOfTax${suffix}`] =
      totalMayBeReclassifiedNetOfTax;

    // 3. Other Comprehensive Income for the Year
    const totalOCI =
      totalNotReclassifiedNetOfTax + totalMayBeReclassifiedNetOfTax;
    calculatedTotals[`ssmt:OtherComprehensiveIncomeForTheYear${suffix}`] =
      totalOCI;

    // 4. Total Comprehensive Income for the Year
    const profitForYear = num(`ssmt:ProfitForTheYearCI${suffix}`);
    calculatedTotals[`ssmt:TotalComprehensiveIncomeForTheYear${suffix}`] =
      profitForYear + totalOCI;

    // 5. Total Comprehensive Income Attribution
    const comprehensiveIncomeToOwners = num(
      `ssmt:ComprehensiveIncomeAttributableToOwnersOfParent${suffix}`,
    );
    const comprehensiveIncomeToNCI = num(
      `ssmt:ComprehensiveIncomeAttributableToNonControllingInterests${suffix}`,
    );
    calculatedTotals[`ssmt:TotalComprehensiveIncomeAttribution${suffix}`] =
      comprehensiveIncomeToOwners + comprehensiveIncomeToNCI;
  }

  return calculatedTotals;
}
